import { z } from "zod";

import { CalcDecimal } from "./decimal.js";
import {
  EQUO_COMPENSO_SCREENING_VERSION,
  classifyEquoCompenso,
  equoCompensoScreeningInputSchema,
} from "./equo-compenso.js";
import { fingerprintReleaseArtifact } from "./release-gate.js";
import {
  DM2016_POST_COMPENSATION_ROUNDING_POLICY_ID,
  DM2016_SERVICE_ROUNDING_POLICY_ID,
  DM2016_SUMMARY_ROUNDING_POLICY_IDS,
  MONEY_ROUNDING_MODE,
  QUOTE_FINANCIALS_ROUNDING_POLICY_ID,
} from "./rounding-policy.js";

export const DM2016_RELEASE_GATE_VERSION = "2.0.0" as const;
export const DM2016_RELEASE_REQUIRED_ARTIFACTS = Object.freeze([
  "z1",
  "z2Coverage",
  "z2Matrix",
  "z2SelectionRules",
  "roundingPolicy",
  "legalScopePolicy",
] as const);
export const DM2016_RELEASE_REQUIRED_NAMED_APPROVALS = Object.freeze([
  "technical_rounding",
  "legal_scope",
] as const);

const decimal = z.string().regex(/^(0|[1-9]\d*)(\.\d+)?$/);
const sha256 = z.string().regex(/^[0-9a-f]{64}$/);

const approvalBaseSchema = z.object({
  second_transcription_by: z.string().min(1).nullable(),
  approved_by: z.string().min(1).nullable(),
  approved_at: z.iso.datetime().nullable(),
  approved_payload_sha256: sha256.nullable(),
});

const z1Schema = z.object({
  status: z.string(),
  safe_for_calculation: z.boolean(),
  source: z.object({ sha256 }),
  approval: approvalBaseSchema.extend({
    first_transcription_by: z.string().min(1),
  }),
  blocking_issues: z.array(z.unknown()),
  categories: z.array(
    z.object({
      code: z.string().min(1),
      destinations: z.array(
        z.object({
          code: z.string().min(1),
          g: decimal,
          description: z.string().min(1).optional(),
        }),
      ),
    }),
  ),
  correspondences_by_code: z.record(
    z.string().min(1),
    z.object({
      law_143_1949: z.array(z.string().min(1)),
      dm_18_11_1971: z.array(z.string().min(1)),
      dm_232_1991: z.array(z.string().min(1)),
      footnote_refs: z.array(z.number().int().positive()),
    }),
  ),
  z1_footnotes: z.array(
    z.object({
      number: z.number().int().positive(),
      text: z.string().min(1),
      applies_to: z.array(z.string().min(1)).min(1),
      execution_status: z.string().min(1),
    }),
  ),
  coverage: z.object({
    destinations_expected: z.number().int(),
    destinations_inventoried: z.number().int(),
    g_values_first_transcription: z.number().int(),
    descriptions_transcribed: z.number().int(),
    correspondence_rows_transcribed: z.number().int(),
    z1_footnotes_transcribed: z.number().int(),
    g_second_transcription_matches: z.number().int(),
    description_second_transcription_matches: z.number().int(),
    correspondence_second_transcription_matches: z.number().int(),
    z1_footnote_second_transcription_matches: z.number().int(),
    second_transcription_matches: z.number().int(),
    calculation_enabled: z.boolean(),
  }),
});

const columnProfileNameSchema = z.enum([
  "GENERAL_8",
  "PRELIMINARY_9",
  "DESIGN_EXECUTION_9",
  "DEFINITIVE_10",
]);

const sourceColumnSchema = z.object({
  source_column: z.string().min(1),
  atomic_scopes: z.array(z.string().min(1)).min(1),
});

const sourceProfilesSchema = z.object({
  GENERAL_8: z.array(sourceColumnSchema).length(8),
  PRELIMINARY_9: z.array(sourceColumnSchema).length(9),
  DESIGN_EXECUTION_9: z.array(sourceColumnSchema).length(9),
  DEFINITIVE_10: z.array(sourceColumnSchema).length(10),
});

const z2CoverageSchema = z.object({
  status: z.string(),
  safe_for_calculation: z.boolean(),
  source: z.object({ sha256 }),
  approval: approvalBaseSchema.extend({
    structural_inventory_by: z.string().min(1),
    numeric_transcription_complete: z.boolean(),
  }),
  blocking_issues: z.array(z.unknown()),
  atomic_scope_order: z.array(z.string().min(1)).length(10),
  source_profiles: sourceProfilesSchema,
  pages: z.array(
    z.object({
      phase_groups: z.array(
        z.object({
          header_profile: columnProfileNameSchema,
          services: z.array(
            z.object({
              code: z.string().min(1),
              tariff_rows: z.number().int().positive(),
            }),
          ),
        }),
      ),
    }),
  ),
  coverage: z.object({
    unique_service_codes_expected: z.number().int(),
    unique_service_codes_inventoried: z.number().int(),
    tariff_rows_expected: z.number().int(),
    tariff_rows_inventoried: z.number().int(),
    normalized_atomic_cells_expected: z.number().int(),
    normalized_atomic_cells_first_transcription: z.number().int(),
    normalized_atomic_numeric_values_first_transcription: z.number().int(),
    normalized_atomic_not_applicable_first_transcription: z.number().int(),
    row_source_profiles_expected: z.number().int(),
    row_source_profiles_inventoried: z.number().int(),
    row_source_profiles_second_transcription_matches: z.number().int(),
    row_source_profiles_remaining_for_second_transcription: z.number().int(),
    row_source_profiles_targeted_reviewed: z.number().int(),
    row_source_profiles_targeted_review_accepted: z.number().int(),
    row_source_profiles_targeted_review_rejected: z.number().int(),
    row_source_profiles_independently_confirmed: z.number().int(),
    row_source_profiles_remaining_for_independent_confirmation: z.number().int(),
    second_transcription_matches: z.number().int(),
    normalized_atomic_cells_remaining_for_first_transcription: z.number().int(),
    normalized_atomic_cells_remaining_for_second_transcription: z.number().int(),
    targeted_review_cells_reviewed: z.number().int(),
    targeted_review_cells_accepted: z.number().int(),
    targeted_review_cells_rejected: z.number().int(),
    normalized_atomic_cells_independently_confirmed: z.number().int(),
    normalized_atomic_cells_remaining_for_independent_confirmation: z.number().int(),
    selection_rules_expected: z.number().int(),
    selection_rules_structured_draft: z.number().int(),
    band_records_expected: z.number().int(),
    band_records_structured_draft: z.number().int(),
    footnotes_expected: z.number().int(),
    footnotes_structured_draft: z.number().int(),
    calculation_enabled: z.boolean(),
  }),
});

const z2MatrixSchema = z.object({
  status: z.string(),
  safe_for_calculation: z.boolean(),
  source: z.object({ sha256 }),
  approval: approvalBaseSchema.extend({
    first_transcription_by: z.string().min(1),
  }),
  atomic_scope_order: z.array(z.string().min(1)).length(10),
  blocking_issues: z.array(z.unknown()),
  services: z.array(
    z.object({
      code: z.string().min(1),
      variants: z.array(
        z.object({
          variant_id: z.string().min(1),
          source_profile: columnProfileNameSchema,
          q: z.array(decimal.nullable()).length(10),
        }),
      ).min(1),
    }),
  ),
});

const selectionVariantSchema = z.object({
  variant_id: z.string().min(1),
  printed_condition_verbatim: z.string().min(1).nullable().optional(),
  application_mode: z.enum([
    "BASE_SLICE",
    "RANGE_SLICE",
    "EXCESS_SLICE",
    "EXCESS_REMAINDER",
  ]),
  lower_bound: decimal.nullable(),
  lower_inclusive: z.boolean(),
  upper_bound: decimal.nullable(),
  upper_inclusive: z.boolean(),
});

const z2SelectionRulesSchema = z.object({
  status: z.string(),
  safe_for_calculation: z.boolean(),
  source: z.object({ sha256 }),
  approval: approvalBaseSchema.extend({
    first_transcription_by: z.string().min(1),
  }),
  blocking_issues: z.array(z.unknown()),
  selection_rules: z.array(
    z.object({
      service_code: z.string().min(1),
      selector_basis: z.enum(["POPULATION_INHABITANTS", "WORK_VALUE_EUR"]),
      calculation_method: z.enum([
        "SUM_PROGRESSIVE_BANDS",
        "SUM_PROGRESSIVE_BANDS_WITH_LINEAR_Q_ON_ACTIVE_PARTIAL_BAND",
      ]),
      interpolation_rule: z.enum([
        "NONE_PRINTED",
        "LINEAR_INTERPOLATION_PRINTED_TARGET_UNRESOLVED",
        "LINEAR_INTERPOLATION_PRINTED_CNG_TIER_METHOD_CANDIDATE",
        "LINEAR_INTERPOLATION_PRINTED_CNG_TIER_METHOD_ADJUDICATED",
      ]),
      execution_status: z.string().min(1),
      interpretation_candidate: z.object({
        method: z.literal("SUM_PROGRESSIVE_BANDS_WITH_LINEAR_Q_ON_ACTIVE_PARTIAL_BAND"),
        band_value: z.literal("USED_PORTION_OF_TOTAL_V"),
        parameter_p_input: z.literal("USED_BAND_VALUE"),
        interpolated_q_precision: z.literal("ROUND_HALF_UP_TO_3_DECIMALS_BEFORE_PRODUCT"),
        evidence_fixture: z.literal("dm2016-cng-geology-active-band-interpolation-v1"),
        scope: z.literal("GEOLOGY_SERVICES_ONLY"),
      }).optional(),
      variants: z.array(selectionVariantSchema).min(2),
    }),
  ),
  footnotes: z.array(
    z.object({
      number: z.number().int(),
      rule_type: z.string().min(1),
      applies_to: z.array(z.string().min(1)).min(1),
      text: z.string().min(1),
      structured_effect: z.record(z.string(), z.unknown()),
      execution_status: z.string().min(1),
    }),
  ),
  coverage: z.object({
    multi_row_services_expected: z.number().int(),
    multi_row_services_structured: z.number().int(),
    band_records_expected: z.number().int(),
    band_records_structured: z.number().int(),
    band_conditions_verbatim_expected: z.number().int(),
    band_conditions_verbatim_first_transcription: z.number().int(),
    footnotes_expected: z.number().int(),
    footnotes_structured: z.number().int(),
    source_transcription_second_matches: z.number().int(),
    source_records_remaining_for_second_transcription: z.number().int(),
    interpretation_second_matches: z.number().int(),
    interpretation_records_remaining_for_second_transcription: z.number().int(),
    second_transcription_matches: z.number().int(),
    rule_records_remaining_for_second_transcription: z.number().int(),
    calculation_enabled: z.boolean(),
  }),
});

const roundingPolicySchema = z.object({
  policy_set_id: z.literal("dm2016-rounding-policy-v1"),
  version: z.string().min(1),
  status: z.string().min(1),
  safe_for_professional_use: z.boolean(),
  money_rounding_mode: z.string().min(1),
  policies: z.object({
    service: z.object({ id: z.string().min(1) }),
    institutional_summary: z.object({
      id: z.string().min(1),
      expected_compensation_eur: decimal,
    }),
    market_comparison_summary: z.object({ id: z.string().min(1) }),
    post_compensation: z.object({ id: z.string().min(1) }),
    quote_financials: z.object({ id: z.string().min(1) }),
  }),
  known_divergence: z.object({
    sum_rounded_service_lines_eur: decimal,
    aggregate_raw_then_round_eur: decimal,
    difference_eur: decimal,
  }),
  release_gate: z.object({
    calculation_enabled: z.boolean(),
    open_issue: z.string().min(1).nullable(),
  }),
  approval: z.object({
    technical_approved_by: z.string().min(1).nullable(),
    approved_at: z.iso.datetime().nullable(),
    approved_payload_sha256: sha256.nullable(),
  }),
});

const legalExpectedSchema = z.object({
  applicability: z.enum([
    "applies",
    "does_not_apply",
    "indeterminate",
    "special_adequacy_rule",
  ]),
  route: z.enum([
    "public_contracts_annex_i13",
    "dm140_protected_client_review",
    "ordinary_negotiated_estimate",
    "collection_agent_adequacy_review",
    "blocked",
  ]),
  estimateUse: z.enum([
    "public_procurement_parameter_base",
    "protected_client_parameter_review",
    "voluntary_parameter_reference",
    "special_adequacy_review",
    "reference_pending_classification",
  ]),
});

const legalScopePolicySchema = z.object({
  matrix_id: z.literal("equo-compenso-legal-scope-v1"),
  version: z.string().min(1),
  status: z.string().min(1),
  safe_for_legal_certification: z.boolean(),
  sources: z.tuple([
    z.object({
      name: z.literal("Legge 21 aprile 2023, n. 49"),
      official_url: z.string().url(),
    }),
    z.object({
      name: z.literal("D.Lgs. 31 marzo 2023, n. 36"),
      official_url: z.string().url(),
    }),
    z.object({
      name: z.literal("D.Lgs. 31 dicembre 2024, n. 209"),
      official_url: z.string().url(),
    }),
  ]),
  private_enterprise_thresholds: z.object({
    logic: z.literal("OR"),
    employees: z.object({ operator: z.literal(">"), value: z.literal(50) }),
    annual_revenue_eur: z.object({
      operator: z.literal(">"),
      value: z.literal("10000000.00"),
    }),
    exact_thresholds_are_inside_scope: z.literal(false),
  }),
  covered_commissioners: z.array(z.string().min(1)),
  special_cases: z.object({
    securitisation_vehicle: z.literal("outside_automatic_law_49_scope"),
    collection_agent: z.literal("special_adequacy_review"),
    private_individual: z.literal("voluntary_parameter_reference"),
    private_enterprise_below_both_thresholds: z.literal("voluntary_parameter_reference"),
    missing_or_incoherent_data: z.literal("reference_pending_classification"),
  }),
  public_procurement: z.object({
    article_108_2_b: z.object({
      fixed_price_share_percent: z.literal("65"),
      discountable_share_percent: z.literal("35"),
      discount_is_automatic: z.literal(false),
    }),
    article_50_1_b: z.object({
      maximum_optional_reduction_percent: z.literal("20"),
      reduction_is_automatic: z.literal(false),
    }),
    parameter_basis: z.literal("Allegato I.13"),
  }),
  product_claims: z.object({
    screening_is_a_legal_opinion: z.literal(false),
    screening_can_certify_fair_compensation: z.literal(false),
    voluntary_parametric_reference_outside_law_49_scope: z.literal(true),
    missing_scope_data_blocks_the_calculation: z.literal(false),
    missing_scope_data_blocks_a_law_49_compliance_label: z.literal(true),
  }),
  executable_cases: z.array(z.object({
    id: z.string().min(1),
    input: equoCompensoScreeningInputSchema,
    expected: legalExpectedSchema,
  })).min(1),
  approval: z.object({
    legal_approved_by: z.string().min(1).nullable(),
    approved_at: z.iso.datetime().nullable(),
    approved_payload_sha256: sha256.nullable(),
  }),
});

export interface Dm2016ReleaseArtifacts {
  readonly z1: unknown;
  readonly z2Coverage: unknown;
  readonly z2Matrix: unknown;
  readonly z2SelectionRules: unknown;
  readonly roundingPolicy: unknown;
  readonly legalScopePolicy: unknown;
}

export interface Dm2016ReleaseGateIssue {
  readonly artifact:
    | "bundle"
    | "z1"
    | "z2Coverage"
    | "z2Matrix"
    | "z2SelectionRules"
    | "roundingPolicy"
    | "legalScopePolicy";
  readonly code: string;
  readonly message: string;
}

export interface Dm2016ReleaseGateResult {
  readonly ready: boolean;
  readonly issues: readonly Dm2016ReleaseGateIssue[];
}

export class Dm2016RulesetReleaseGateError extends Error {
  readonly issues: readonly Dm2016ReleaseGateIssue[];

  constructor(issues: readonly Dm2016ReleaseGateIssue[]) {
    super("D.M. 17 giugno 2016 ruleset is not approved for calculation");
    this.name = "Dm2016RulesetReleaseGateError";
    this.issues = issues;
  }
}

const expectedAtomicScopes = [
  "EDILIZIA",
  "STRUTTURE_S01_S03",
  "STRUTTURE_S02_S04_S05_S06",
  "IMPIANTI_IA",
  "IMPIANTI_IB",
  "VIABILITA",
  "IDRAULICA",
  "TIC",
  "AGRICOLTURA_AMBIENTE",
  "TERRITORIO_URBANISTICA",
] as const;

function numberedCodes(prefix: string, from: number, to: number): string[] {
  return Array.from(
    { length: to - from + 1 },
    (_, index) => `${prefix}.${String(from + index).padStart(2, "0")}`,
  );
}

const expectedZ1Codes = [
  ...numberedCodes("E", 1, 22),
  ...numberedCodes("S", 1, 6),
  ...numberedCodes("IA", 1, 4),
  ...numberedCodes("IB", 4, 12),
  ...numberedCodes("V", 1, 3),
  ...numberedCodes("D", 1, 5),
  ...numberedCodes("T", 1, 3),
  ...numberedCodes("P", 1, 6),
  ...numberedCodes("U", 1, 3),
] as const;

const expectedZ2Codes = [
  ...numberedCodes("Qa.0", 1, 7),
  ...numberedCodes("QaI", 1, 3),
  ...numberedCodes("QaII", 1, 3),
  ...numberedCodes("QaIII", 1, 3),
  "QaIV.01",
  ...numberedCodes("QbI", 1, 20),
  ...numberedCodes("QbII", 1, 27),
  ...numberedCodes("QbIII", 1, 11),
  ...numberedCodes("QcI", 1, 5),
  "QcI.05.01",
  ...numberedCodes("QcI", 6, 13),
  ...numberedCodes("QdI", 1, 5),
  ...numberedCodes("QeI", 1, 2),
] as const;

const expectedLegalCaseIds = [
  "bank-protected",
  "enterprise-51-workers",
  "enterprise-revenue-over-threshold",
  "enterprise-exact-thresholds",
  "enterprise-incomplete-data",
  "private-individual-reference",
  "tusp-company-protected",
  "public-best-value",
  "public-direct-award",
  "securitisation-exclusion",
  "collection-agent-special-rule",
] as const;

const expectedCoveredCommissioners = [
  "bank",
  "insurance_company",
  "bank_or_insurance_controlled_or_mandated",
  "private_enterprise_above_either_threshold",
  "public_administration",
  "company_governed_by_legislative_decree_175_2016",
] as const;

const forbiddenPlaceholderFragments = [
  "standard processing applied",
  "filler.",
] as const;

function containsForbiddenPlaceholder(value: unknown): boolean {
  if (typeof value === "string") {
    const normalised = value.trim().toLowerCase();
    return forbiddenPlaceholderFragments.some((fragment) =>
      normalised.includes(fragment)
    );
  }
  if (Array.isArray(value)) return value.some(containsForbiddenPlaceholder);
  if (value !== null && typeof value === "object")
    return Object.entries(value).some(
      ([key, child]) =>
        containsForbiddenPlaceholder(key) || containsForbiddenPlaceholder(child),
    );
  return false;
}

function sameOrderedValues(
  actual: readonly string[],
  expected: readonly string[],
): boolean {
  return actual.length === expected.length
    && actual.every((value, index) => value === expected[index]);
}

function addIssue(
  issues: Dm2016ReleaseGateIssue[],
  artifact: Dm2016ReleaseGateIssue["artifact"],
  code: string,
  message: string,
): void {
  issues.push({ artifact, code, message });
}

function unique(values: readonly string[]): boolean {
  return new Set(values).size === values.length;
}

function checkCommon(
  artifact: "z1" | "z2Coverage" | "z2Matrix" | "z2SelectionRules",
  value: {
    status: string;
    safe_for_calculation: boolean;
    blocking_issues: unknown[];
    approval: {
      second_transcription_by: string | null;
      approved_by: string | null;
      approved_at: string | null;
    };
  },
  issues: Dm2016ReleaseGateIssue[],
  secondTranscriptionRequired = true,
): void {
  if (value.status !== "approved")
    addIssue(issues, artifact, "STATUS_NOT_APPROVED", "Status must be approved");
  if (!value.safe_for_calculation)
    addIssue(issues, artifact, "SAFE_FLAG_FALSE", "safe_for_calculation must be true");
  if (value.blocking_issues.length !== 0)
    addIssue(issues, artifact, "BLOCKING_ISSUES_OPEN", "Blocking issues must be empty");
  if (secondTranscriptionRequired && value.approval.second_transcription_by === null)
    addIssue(issues, artifact, "SECOND_TRANSCRIPTION_MISSING", "Second transcriber is required");
  if (value.approval.approved_by === null)
    addIssue(issues, artifact, "APPROVER_MISSING", "Approver is required");
  if (value.approval.approved_at === null)
    addIssue(issues, artifact, "APPROVAL_DATE_MISSING", "Approval timestamp is required");
}

async function checkFingerprint(
  artifact: Exclude<Dm2016ReleaseGateIssue["artifact"], "bundle">,
  raw: unknown,
  expected: string | null,
  issues: Dm2016ReleaseGateIssue[],
): Promise<void> {
  if (expected === null) {
    addIssue(
      issues,
      artifact,
      "APPROVED_FINGERPRINT_MISSING",
      "Missing approved payload fingerprint",
    );
    return;
  }
  if ((await fingerprintReleaseArtifact(raw)) !== expected) {
    addIssue(
      issues,
      artifact,
      "APPROVED_FINGERPRINT_MISMATCH",
      "Artifact changed after approval fingerprinting",
    );
  }
}

export async function evaluateDm2016ReleaseGate(
  artifacts: Dm2016ReleaseArtifacts,
): Promise<Dm2016ReleaseGateResult> {
  const issues: Dm2016ReleaseGateIssue[] = [];

  for (const [artifact, raw] of Object.entries(artifacts) as Array<
    [Exclude<Dm2016ReleaseGateIssue["artifact"], "bundle">, unknown]
  >) {
    if (containsForbiddenPlaceholder(raw))
      addIssue(
        issues,
        artifact,
        "PLACEHOLDER_CONTENT_FORBIDDEN",
        "FILLER and synthetic processing placeholders are forbidden in release artifacts",
      );
  }

  const z1Result = z1Schema.safeParse(artifacts.z1);
  const coverageResult = z2CoverageSchema.safeParse(artifacts.z2Coverage);
  const matrixResult = z2MatrixSchema.safeParse(artifacts.z2Matrix);
  const selectionResult = z2SelectionRulesSchema.safeParse(artifacts.z2SelectionRules);
  const roundingResult = roundingPolicySchema.safeParse(artifacts.roundingPolicy);
  const legalResult = legalScopePolicySchema.safeParse(artifacts.legalScopePolicy);

  if (!z1Result.success)
    addIssue(issues, "z1", "STRUCTURE_INVALID", z1Result.error.message);
  if (!coverageResult.success)
    addIssue(issues, "z2Coverage", "STRUCTURE_INVALID", coverageResult.error.message);
  if (!matrixResult.success) {
    addIssue(
      issues,
      "z2Matrix",
      artifacts.z2Matrix === null || artifacts.z2Matrix === undefined
        ? "Z2_MATRIX_MISSING"
        : "STRUCTURE_INVALID",
      artifacts.z2Matrix === null || artifacts.z2Matrix === undefined
        ? "The complete 2016 Z-2 matrix is required"
        : matrixResult.error.message,
    );
  }
  if (!selectionResult.success) {
    addIssue(
      issues,
      "z2SelectionRules",
      artifacts.z2SelectionRules === null || artifacts.z2SelectionRules === undefined
        ? "Z2_SELECTION_RULES_MISSING"
        : "STRUCTURE_INVALID",
      artifacts.z2SelectionRules === null || artifacts.z2SelectionRules === undefined
        ? "The complete 2016 Z-2 selection and footnote rules are required"
        : selectionResult.error.message,
    );
  }
  if (!roundingResult.success) {
    addIssue(
      issues,
      "roundingPolicy",
      artifacts.roundingPolicy === null || artifacts.roundingPolicy === undefined
        ? "ROUNDING_POLICY_MISSING"
        : "STRUCTURE_INVALID",
      artifacts.roundingPolicy === null || artifacts.roundingPolicy === undefined
        ? "The approved D.M. 2016 rounding policy is required"
        : roundingResult.error.message,
    );
  }
  if (!legalResult.success) {
    addIssue(
      issues,
      "legalScopePolicy",
      artifacts.legalScopePolicy === null || artifacts.legalScopePolicy === undefined
        ? "LEGAL_SCOPE_POLICY_MISSING"
        : "STRUCTURE_INVALID",
      artifacts.legalScopePolicy === null || artifacts.legalScopePolicy === undefined
        ? "The approved equo-compenso legal scope policy is required"
        : legalResult.error.message,
    );
  }
  if (
    !z1Result.success
    || !coverageResult.success
    || !matrixResult.success
    || !selectionResult.success
    || !roundingResult.success
    || !legalResult.success
  )
    return { ready: false, issues };

  const z1 = z1Result.data;
  const coverage = coverageResult.data;
  const matrix = matrixResult.data;
  const selection = selectionResult.data;
  const rounding = roundingResult.data;
  const legal = legalResult.data;
  checkCommon("z1", z1, issues);
  checkCommon("z2Coverage", coverage, issues);
  // The matrix is the frozen first-transcription artifact. Independent numeric
  // evidence lives in z2Coverage so its historical blind-review hash remains
  // stable even after adjudication.
  checkCommon("z2Matrix", matrix, issues, false);
  checkCommon("z2SelectionRules", selection, issues);

  if (rounding.status !== "approved")
    addIssue(issues, "roundingPolicy", "STATUS_NOT_APPROVED", "Status must be approved");
  if (!rounding.safe_for_professional_use)
    addIssue(issues, "roundingPolicy", "SAFE_FLAG_FALSE", "safe_for_professional_use must be true");
  if (!rounding.release_gate.calculation_enabled)
    addIssue(issues, "roundingPolicy", "ROUNDING_POLICY_CALCULATION_DISABLED", "Rounding policy calculation flag must be enabled");
  if (rounding.release_gate.open_issue !== null)
    addIssue(issues, "roundingPolicy", "ROUNDING_POLICY_ISSUE_OPEN", "Rounding policy must not retain an open release issue");
  if (rounding.approval.technical_approved_by === null)
    addIssue(issues, "roundingPolicy", "TECHNICAL_APPROVER_MISSING", "Named technical approver is required");
  if (rounding.approval.approved_at === null)
    addIssue(issues, "roundingPolicy", "APPROVAL_DATE_MISSING", "Approval timestamp is required");

  if (legal.status !== "approved")
    addIssue(issues, "legalScopePolicy", "STATUS_NOT_APPROVED", "Status must be approved");
  if (!legal.safe_for_legal_certification)
    addIssue(issues, "legalScopePolicy", "SAFE_FLAG_FALSE", "safe_for_legal_certification must be true");
  if (legal.approval.legal_approved_by === null)
    addIssue(issues, "legalScopePolicy", "LEGAL_APPROVER_MISSING", "Named legal approver is required");
  if (legal.approval.approved_at === null)
    addIssue(issues, "legalScopePolicy", "APPROVAL_DATE_MISSING", "Approval timestamp is required");

  if (
    rounding.money_rounding_mode !== MONEY_ROUNDING_MODE
    || rounding.policies.service.id !== DM2016_SERVICE_ROUNDING_POLICY_ID
    || rounding.policies.institutional_summary.id
      !== DM2016_SUMMARY_ROUNDING_POLICY_IDS.sum_rounded_lines
    || rounding.policies.market_comparison_summary.id
      !== DM2016_SUMMARY_ROUNDING_POLICY_IDS.aggregate_raw
    || rounding.policies.post_compensation.id
      !== DM2016_POST_COMPENSATION_ROUNDING_POLICY_ID
    || rounding.policies.quote_financials.id
      !== QUOTE_FINANCIALS_ROUNDING_POLICY_ID
    || rounding.policies.institutional_summary.expected_compensation_eur !== "31531.73"
    || rounding.known_divergence.sum_rounded_service_lines_eur !== "31531.73"
    || rounding.known_divergence.aggregate_raw_then_round_eur !== "31531.72"
    || rounding.known_divergence.difference_eur !== "0.01"
  )
    addIssue(
      issues,
      "roundingPolicy",
      "ROUNDING_POLICY_SEMANTICS_INVALID",
      "The approved rounding policy must match the implemented policy identities and official one-cent fixture",
    );

  if (legal.version !== EQUO_COMPENSO_SCREENING_VERSION)
    addIssue(
      issues,
      "legalScopePolicy",
      "LEGAL_SCOPE_VERSION_MISMATCH",
      "Legal scope matrix and executable screening versions must match",
    );
  if (
    !sameOrderedValues(
      legal.executable_cases.map((fixture) => fixture.id),
      expectedLegalCaseIds,
    )
    || !sameOrderedValues(legal.covered_commissioners, expectedCoveredCommissioners)
  )
    addIssue(
      issues,
      "legalScopePolicy",
      "LEGAL_SCOPE_CASE_INVENTORY_INVALID",
      "Legal scope policy must preserve all covered commissioner classes and eleven boundary cases",
    );
  if (legal.executable_cases.some((fixture) => {
    const actual = classifyEquoCompenso(fixture.input);
    return actual.applicability !== fixture.expected.applicability
      || actual.route !== fixture.expected.route
      || actual.estimateUse !== fixture.expected.estimateUse
      || actual.certifiedEquoCompensoAllowed !== false;
  }))
    addIssue(
      issues,
      "legalScopePolicy",
      "LEGAL_SCOPE_EXECUTABLE_CASE_MISMATCH",
      "Legal scope fixtures must match the executable classifier without enabling certification",
    );

  if (z1.approval.second_transcription_by === z1.approval.first_transcription_by)
    addIssue(issues, "z1", "TRANSCRIBERS_NOT_INDEPENDENT", "First and second Z-1 transcribers must differ");
  if (matrix.approval.second_transcription_by === matrix.approval.first_transcription_by)
    addIssue(issues, "z2Matrix", "TRANSCRIBERS_NOT_INDEPENDENT", "First and second Z-2 transcribers must differ");
  if (coverage.approval.second_transcription_by === matrix.approval.first_transcription_by)
    addIssue(issues, "bundle", "TRANSCRIBERS_NOT_INDEPENDENT", "The independent Z-2 reviewer must differ from the matrix first transcriber");
  if (selection.approval.second_transcription_by === selection.approval.first_transcription_by)
    addIssue(issues, "z2SelectionRules", "TRANSCRIBERS_NOT_INDEPENDENT", "First and second Z-2 rule transcribers must differ");
  if (!coverage.approval.numeric_transcription_complete)
    addIssue(issues, "z2Coverage", "NUMERIC_TRANSCRIPTION_INCOMPLETE", "Numeric transcription must be complete");

  const destinations = z1.categories.flatMap((category) => category.destinations);
  const destinationCodes = destinations.map((destination) => destination.code);
  const describedDestinations = destinations.filter(
    (destination) => destination.description !== undefined,
  ).length;
  const correspondenceCodes = Object.keys(z1.correspondences_by_code);
  if (
    destinations.length !== 61 ||
    z1.coverage.destinations_expected !== 61 ||
    z1.coverage.destinations_inventoried !== 61 ||
    z1.coverage.g_values_first_transcription !== 61
  )
    addIssue(issues, "z1", "Z1_COVERAGE_INVALID", "Z-1 must contain 61 code/G pairs");
  if (!unique(destinationCodes))
    addIssue(issues, "z1", "Z1_CODES_DUPLICATED", "Z-1 destination codes must be unique");
  if (!sameOrderedValues(destinationCodes, expectedZ1Codes))
    addIssue(
      issues,
      "z1",
      "Z1_OFFICIAL_CODE_INVENTORY_INVALID",
      "Z-1 must contain the exact 61 official destination codes in source order",
    );
  if (destinations.some((destination) => new CalcDecimal(destination.g).lessThanOrEqualTo(0)))
    addIssue(issues, "z1", "Z1_G_INVALID", "Every Z-1 G value must be greater than zero");
  if (describedDestinations !== 61 || z1.coverage.descriptions_transcribed !== 61)
    addIssue(issues, "z1", "Z1_DESCRIPTIONS_INCOMPLETE", "All 61 official descriptions are required");
  if (
    !sameOrderedValues(correspondenceCodes, expectedZ1Codes)
    || z1.coverage.correspondence_rows_transcribed !== 61
  )
    addIssue(
      issues,
      "z1",
      "Z1_CORRESPONDENCES_INCOMPLETE",
      "Correspondence records must cover the exact 61 official codes in source order",
    );
  if (
    z1.z1_footnotes.length !== 1
    || z1.z1_footnotes[0]?.number !== 1
    || !sameOrderedValues(
      z1.z1_footnotes[0]?.applies_to ?? [],
      ["IA.01", "IA.02", "IA.03", "IA.04"],
    )
    || z1.coverage.z1_footnotes_transcribed !== 1
  )
    addIssue(
      issues,
      "z1",
      "Z1_FOOTNOTE_INCOMPLETE",
      "Z-1 footnote 1 and its four printed references are required",
    );
  if (
    z1.coverage.g_second_transcription_matches !== 61
    || z1.coverage.description_second_transcription_matches !== 61
    || z1.coverage.correspondence_second_transcription_matches !== 61
    || z1.coverage.z1_footnote_second_transcription_matches !== 1
    || z1.coverage.second_transcription_matches !== 61
  )
    addIssue(
      issues,
      "z1",
      "Z1_SECOND_MATCHES_INCOMPLETE",
      "G, descriptions, correspondences and Z-1 footnote 1 must all match the independent transcription",
    );
  if (!z1.coverage.calculation_enabled)
    addIssue(issues, "z1", "Z1_CALCULATION_DISABLED", "Z-1 calculation flag must be enabled");

  const inventoryServices = coverage.pages.flatMap((page) =>
    page.phase_groups.flatMap((phase) =>
      phase.services,
    ),
  );
  const inventoryCodes = inventoryServices.map((service) => service.code);
  const matrixCodes = matrix.services.map((service) => service.code);
  if (!unique(inventoryCodes) || !unique(matrixCodes))
    addIssue(issues, "bundle", "Z2_CODES_DUPLICATED", "Z-2 service codes must be unique");
  if (
    inventoryCodes.length !== 96 ||
    matrixCodes.length !== 96 ||
    inventoryCodes.some((code, index) => code !== matrixCodes[index])
  )
    addIssue(issues, "bundle", "Z2_CODE_INVENTORY_MISMATCH", "Coverage and matrix must contain the same 96 ordered service codes");
  if (
    !sameOrderedValues(inventoryCodes, expectedZ2Codes)
    || !sameOrderedValues(matrixCodes, expectedZ2Codes)
  )
    addIssue(
      issues,
      "bundle",
      "Z2_OFFICIAL_CODE_INVENTORY_INVALID",
      "Z-2 must contain the exact 96 official service codes in source order",
    );

  if (
    expectedAtomicScopes.some(
      (scope, index) =>
        coverage.atomic_scope_order[index] !== scope ||
        matrix.atomic_scope_order[index] !== scope,
    )
  )
    addIssue(issues, "bundle", "ATOMIC_SCOPE_ORDER_INVALID", "The ten normalized Z-2 scopes must use the official order and splits");

  const variantAccountingValid = inventoryServices.every((inventoryService, index) => {
    const matrixService = matrix.services[index];
    if (
      matrixService === undefined ||
      matrixService.variants.length !== inventoryService.tariff_rows
    ) return false;
    return matrixService.variants.every((variant) => variant.q.length === 10);
  });
  if (!variantAccountingValid)
    addIssue(issues, "bundle", "Z2_VARIANT_INVENTORY_MISMATCH", "Every service must preserve its official tariff rows and ten normalized scopes");

  const allMatrixCells = matrix.services.flatMap((service) =>
    service.variants.flatMap((variant) => variant.q),
  );
  const numericCells = allMatrixCells
    .filter((value) => value !== null).length;
  const nullCells = allMatrixCells.length - numericCells;
  const inventoryTariffRows = inventoryServices.reduce(
    (sum, service) => sum + service.tariff_rows,
    0,
  );
  const inventoryCells = inventoryTariffRows * 10;
  const c = coverage.coverage;
  if (
    c.unique_service_codes_expected !== 96 ||
    c.unique_service_codes_inventoried !== 96 ||
    c.tariff_rows_expected !== 129 ||
    c.tariff_rows_inventoried !== 129 ||
    inventoryTariffRows !== 129 ||
    c.normalized_atomic_cells_expected !== 1290 ||
    inventoryCells !== 1290 ||
    c.normalized_atomic_cells_first_transcription !== 1290 ||
    c.row_source_profiles_expected !== 129 ||
    c.row_source_profiles_inventoried !== 129 ||
    c.selection_rules_expected !== 13 ||
    c.selection_rules_structured_draft !== 13 ||
    c.band_records_expected !== 46 ||
    c.band_records_structured_draft !== 46 ||
    c.footnotes_expected !== 12 ||
    c.footnotes_structured_draft !== 12
  )
    addIssue(issues, "z2Coverage", "Z2_COVERAGE_INVALID", "Z-2 must declare 96 services, 129 tariff rows, 129 source profiles and 1,290 normalized cells");
  if (
    allMatrixCells.length !== 1290 ||
    c.normalized_atomic_numeric_values_first_transcription !== numericCells ||
    c.normalized_atomic_not_applicable_first_transcription !== nullCells ||
    numericCells + nullCells !== 1290
  )
    addIssue(issues, "bundle", "Z2_CELL_ACCOUNTING_INVALID", "Every Z-2 coordinate must be numeric or explicitly not applicable");
  if (
    c.second_transcription_matches + c.normalized_atomic_cells_remaining_for_second_transcription !== 1290
    || c.targeted_review_cells_accepted + c.targeted_review_cells_rejected !== c.targeted_review_cells_reviewed
    || c.normalized_atomic_cells_independently_confirmed
      + c.normalized_atomic_cells_remaining_for_independent_confirmation !== 1290
    || c.row_source_profiles_second_transcription_matches
      + c.row_source_profiles_remaining_for_second_transcription !== 129
    || c.row_source_profiles_targeted_review_accepted
      + c.row_source_profiles_targeted_review_rejected
      !== c.row_source_profiles_targeted_reviewed
    || c.row_source_profiles_second_transcription_matches
      + c.row_source_profiles_targeted_review_accepted
      !== c.row_source_profiles_independently_confirmed
    || c.row_source_profiles_independently_confirmed
      + c.row_source_profiles_remaining_for_independent_confirmation !== 129
  )
    addIssue(issues, "z2Coverage", "Z2_REVIEW_ACCOUNTING_INVALID", "Second-transcription and documented-adjudication counts must reconcile exactly");
  if (
    c.row_source_profiles_independently_confirmed !== 129 ||
    c.row_source_profiles_remaining_for_independent_confirmation !== 0
  )
    addIssue(
      issues,
      "z2Coverage",
      "Z2_SOURCE_PROFILE_MATCHES_INCOMPLETE",
      "All 129 row-level source column profiles must match an independent transcription or a documented source adjudication",
    );
  if (
    c.normalized_atomic_cells_independently_confirmed !== 1290 ||
    c.normalized_atomic_cells_remaining_for_first_transcription !== 0 ||
    c.normalized_atomic_cells_remaining_for_independent_confirmation !== 0
  )
    addIssue(issues, "z2Coverage", "Z2_INDEPENDENT_CONFIRMATION_INCOMPLETE", "All 1,290 normalized Z-2 cells must match an independent transcription or a documented blind adjudication");
  if (!c.calculation_enabled)
    addIssue(issues, "z2Coverage", "Z2_CALCULATION_DISABLED", "Z-2 calculation flag must be enabled");

  const multiRowServices = matrix.services.filter((service) => service.variants.length > 1);
  const selectionCodes = selection.selection_rules.map((rule) => rule.service_code);
  const selectionVariantAccountingValid = multiRowServices.every((service, index) => {
    const rule = selection.selection_rules[index];
    return rule !== undefined
      && rule.service_code === service.code
      && rule.variants.length === service.variants.length
      && rule.variants.every(
        (variant, variantIndex) =>
          variant.variant_id === service.variants[variantIndex]?.variant_id,
      );
  });
  const footnoteNumbers = selection.footnotes.map((footnote) => footnote.number);
  const sc = selection.coverage;
  if (
    multiRowServices.length !== 13
    || selection.selection_rules.length !== 13
    || !unique(selectionCodes)
    || !selectionVariantAccountingValid
    || selection.selection_rules.flatMap((rule) => rule.variants).length !== 46
    || footnoteNumbers.length !== 12
    || footnoteNumbers.some((number, index) => number !== index + 2)
    || sc.multi_row_services_expected !== 13
    || sc.multi_row_services_structured !== 13
    || sc.band_records_expected !== 46
    || sc.band_records_structured !== 46
    || sc.band_conditions_verbatim_expected !== 46
    || sc.footnotes_expected !== 12
    || sc.footnotes_structured !== 12
  )
    addIssue(issues, "z2SelectionRules", "Z2_SELECTION_COVERAGE_INVALID", "Z-2 selection rules must cover 13 multi-row services, 46 bands and footnotes 2-13 in matrix order");
  if (
    sc.band_conditions_verbatim_first_transcription !== 46
    || selection.selection_rules.some((rule) =>
      rule.variants.some((variant) =>
        variant.printed_condition_verbatim === undefined
        || variant.printed_condition_verbatim === null
        || variant.printed_condition_verbatim.trim().length === 0,
      ),
    )
  )
    addIssue(
      issues,
      "z2SelectionRules",
      "Z2_SELECTION_FIRST_SOURCE_TRANSCRIPTION_INCOMPLETE",
      "All 46 printed band conditions require a verbatim first transcription before an independent source match can be accepted",
    );
  if (
    sc.source_transcription_second_matches !== 58
    || sc.source_records_remaining_for_second_transcription !== 0
  )
    addIssue(
      issues,
      "z2SelectionRules",
      "Z2_SELECTION_SOURCE_MATCHES_INCOMPLETE",
      "All 46 printed bands and footnotes 2-13 must match an independent source transcription",
    );
  if (
    sc.interpretation_second_matches !== 58
    || sc.interpretation_records_remaining_for_second_transcription !== 0
  )
    addIssue(
      issues,
      "z2SelectionRules",
      "Z2_SELECTION_INTERPRETATION_MATCHES_INCOMPLETE",
      "All 58 executable interpretations must match an independent technical review",
    );
  if (
    selection.selection_rules.some((rule) => rule.execution_status !== "approved_executable")
    || selection.footnotes.some((footnote) => footnote.execution_status !== "approved_executable")
    || sc.second_transcription_matches !== 58
    || sc.rule_records_remaining_for_second_transcription !== 0
    || !sc.calculation_enabled
  )
    addIssue(issues, "z2SelectionRules", "Z2_SELECTION_RULES_NOT_APPROVED", "All 58 band and footnote records must be independently matched and approved as executable");

  const geologyServices = new Set(["QbI.11", "QbII.13", "QcI.05.01"]);
  if (selection.selection_rules.some((rule) => {
    const isGeology = geologyServices.has(rule.service_code);
    return isGeology
      ? rule.calculation_method !== "SUM_PROGRESSIVE_BANDS_WITH_LINEAR_Q_ON_ACTIVE_PARTIAL_BAND"
        || rule.interpolation_rule !== "LINEAR_INTERPOLATION_PRINTED_CNG_TIER_METHOD_ADJUDICATED"
        || rule.interpretation_candidate === undefined
      : rule.calculation_method !== "SUM_PROGRESSIVE_BANDS"
        || rule.interpolation_rule !== "NONE_PRINTED"
        || rule.interpretation_candidate !== undefined;
  }))
    addIssue(
      issues,
      "z2SelectionRules",
      "Z2_SELECTION_EXECUTABLE_SEMANTICS_INVALID",
      "Progressive and CNG geology services must retain their adjudicated calculation method and three-decimal interpolated-Q policy",
    );

  if (new Set([z1.source.sha256, coverage.source.sha256, matrix.source.sha256, selection.source.sha256]).size !== 1)
    addIssue(issues, "bundle", "SOURCE_HASH_MISMATCH", "All artifacts must reference the same official source hash");

  await Promise.all([
    checkFingerprint("z1", artifacts.z1, z1.approval.approved_payload_sha256, issues),
    checkFingerprint("z2Coverage", artifacts.z2Coverage, coverage.approval.approved_payload_sha256, issues),
    checkFingerprint("z2Matrix", artifacts.z2Matrix, matrix.approval.approved_payload_sha256, issues),
    checkFingerprint("z2SelectionRules", artifacts.z2SelectionRules, selection.approval.approved_payload_sha256, issues),
    checkFingerprint("roundingPolicy", artifacts.roundingPolicy, rounding.approval.approved_payload_sha256, issues),
    checkFingerprint("legalScopePolicy", artifacts.legalScopePolicy, legal.approval.approved_payload_sha256, issues),
  ]);

  return { ready: issues.length === 0, issues };
}

export async function assertDm2016ReleaseReady(
  artifacts: Dm2016ReleaseArtifacts,
): Promise<void> {
  const result = await evaluateDm2016ReleaseGate(artifacts);
  if (!result.ready) throw new Dm2016RulesetReleaseGateError(result.issues);
}
